import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiTrendingUp, FiUsers, FiTarget, FiPhone } = FiIcons;

const FinalClosingSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Emotional Icons */}
          <div className="flex items-center justify-center space-x-8 mb-12">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center"
            >
              <SafeIcon icon={FiHeart} className="w-8 h-8 text-red-400" />
            </motion.div>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center"
            >
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-green-400" />
            </motion.div>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
              className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center"
            >
              <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-400" />
            </motion.div>
          </div>

          {/* Main Emotional Message */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Your business <span className="text-red-400">deserves to grow</span>.<br />
              Your family <span className="text-green-400">deserves stability</span>.<br />
              Your customers <span className="text-blue-400">deserve a better experience</span>.
            </h2>
            
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-3xl p-8 mb-8 backdrop-blur-lg">
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Let FunnelsDone make your business <span className="text-yellow-400">unstoppable</span>.
              </h3>
            </div>
          </motion.div>

          {/* Value Propositions */}
          <motion.div 
            className="grid md:grid-cols-3 gap-8 mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <SafeIcon icon={FiHeart} className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-white mb-3">For Your Business</h4>
              <p className="text-gray-200">Stop struggling with leads, follow-ups, and manual processes. Automate everything and focus on what you love - growing your business.</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-white mb-3">For Your Family</h4>
              <p className="text-gray-200">Create predictable income, reduce stress, and have more time for what matters most. Build the financial security your family deserves.</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <SafeIcon icon={FiUsers} className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-white mb-3">For Your Customers</h4>
              <p className="text-gray-200">Provide instant responses, professional service, and seamless experiences. Turn customers into loyal advocates who refer others.</p>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-400/30 rounded-3xl p-12 backdrop-blur-lg">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">
                The question isn't whether you can afford FunnelsDone...
              </h3>
              <h4 className="text-3xl lg:text-4xl font-bold text-yellow-400 mb-8">
                The question is: Can you afford NOT to have it?
              </h4>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mb-6"
              >
                <a 
                  href="https://funnelsdone.com/cash" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-12 py-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-2xl rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25"
                >
                  👉 Book Your Free Growth Strategy Call
                  <SafeIcon icon={FiPhone} className="ml-3 w-7 h-7" />
                </a>
              </motion.div>

              <div className="text-gray-300 space-y-2">
                <p className="text-lg">⏰ <strong>Limited spots available</strong> - Only 20 calls per week</p>
                <p>💡 <strong>100% Free consultation</strong> - No strings attached</p>
                <p>🚀 <strong>Custom growth blueprint</strong> - Tailored to your business</p>
              </div>
            </div>
          </motion.div>

          {/* Trust Elements */}
          <motion.div 
            className="flex items-center justify-center space-x-8 mt-12 text-gray-300"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-white">500+</div>
              <div className="text-sm">Success Stories</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">4.9★</div>
              <div className="text-sm">Rating</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-sm">Support</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">₹50L+</div>
              <div className="text-sm">Revenue Generated</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalClosingSection;